import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo } from "react";
import {
  useCancelPrivateLessonBooking,
  usePrivateLessonBookingsQuery,
} from "../api/queries";
import { BookingsFilter } from "../ui/BookingsFilter";
import { BookingsTable } from "../ui/BookingsTable";
import { BookingsToolbar } from "../ui/BookingsToolbar";

export function PrivateLessonBookingsPage() {
  const nav = useNavigate({ from: "/app/private-lessons/bookings" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/private-lessons/bookings",
  });

  const query = useMemo<OffsetPageQueryDTO>(
    () => ({
      limit: search.limit ?? 20,
      offset: search.offset ?? 0,
      sort: search.sort,
      filters: search.filters,
    }),
    [search.limit, search.offset, search.sort, search.filters],
  );

  const list = usePrivateLessonBookingsQuery(query);
  const canManage = useActionPermission(["private-lessons:create"]);
  const cancelBooking = useCancelPrivateLessonBooking();

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ການຈອງຮຽນສ່ວນຕົວ" },
          ]}
        />
      </Header>

      <Main>
        <BookingsToolbar
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/private-lessons/bookings/create" })}
          onBulkCreate={() =>
            nav({ to: "/app/private-lessons/bookings/bulk-create" })
          }
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <BookingsFilter />

          <BookingsTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onView={(booking) =>
              nav({
                to: "/app/private-lessons/bookings/$id",
                params: { id: booking.id },
              })
            }
            onEdit={(booking) =>
              nav({
                to: "/app/private-lessons/bookings/$id/edit",
                params: { id: booking.id },
              })
            }
            onCancel={async (id) => {
              toast.promise(cancelBooking.mutateAsync(id), {
                loading: "ກຳລັງຍົກເລີກ...",
                success: "ຍົກເລີກການຈອງສຳເລັດ",
                error: "ຍົກເລີກການຈອງລົ້ມເຫຼວ",
              });
            }}
          />
        </div>
      </Main>
    </>
  );
}
